import { publicAxios } from './axiosConfig';

/**
 * CSRF Token Utility
 *
 * Reads CSRF token from cookie (when available) and keeps an in-memory copy
 * so that it also works in production when the API is on a different subdomain
 * (e.g. api.cognidaily.io) and the cookie is not directly readable via
 * document.cookie from the frontend origin.
 */

// In-memory fallback for environments where the CSRF cookie is not readable
let inMemoryCsrfToken: string | null = null;

/**
 * Get CSRF token from cookie or in-memory cache
 */
export function getCsrfToken(): string | null {
  const name = 'csrf-token=';

  // In non-browser environments (tests, SSR), fall back to in-memory token
  if (typeof document === 'undefined') {
    return inMemoryCsrfToken;
  }

  const decodedCookie = decodeURIComponent(document.cookie || '');
  const cookieArray = decodedCookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      const token = cookie.substring(name.length, cookie.length);
      // Keep an in-memory copy so we can still use it even if the cookie
      // becomes unreadable due to subdomain/Origin differences.
      inMemoryCsrfToken = token;
      return token;
    }
  }

  // Fall back to in-memory token if cookie isn't directly readable
  return inMemoryCsrfToken;
}

/**
 * Get CSRF token header value for axios
 */
export function getCsrfHeader(): { 'x-csrf-token': string } | {} {
  const token = getCsrfToken();
  if (token) {
    return { 'x-csrf-token': token };
  }
  return {};
}

/**
 * Ensure CSRF token is available before making POST/PUT/DELETE requests.
 *
 * This will:
 * - Return an existing token from cookie/memory when available
 * - Otherwise call the backend /api/auth/csrf-token endpoint (using the same
 *   base URL configuration as axios) to obtain a fresh token
 * - Store the token in memory so it can be reused even if document.cookie
 *   cannot see the cookie due to subdomain differences
 *
 * The optional forceRefresh flag can be used after CSRF errors to force
 * retrieval of a fresh token.
 */
export async function ensureCsrfToken(forceRefresh: boolean = false): Promise<string | null> {
  // Reuse existing token unless we explicitly want a fresh one
  if (!forceRefresh) {
    const existing = getCsrfToken();
    if (existing) {
      return existing;
    }
  }

  try {
    const response = await publicAxios.get('/api/auth/csrf-token');
    const data: any = response.data;
    const tokenFromResponse: string | undefined = data?.data?.token;

    // Prefer token from response body, but fall back to cookie if needed
    const token = tokenFromResponse || getCsrfToken() || null;

    if (token) {
      inMemoryCsrfToken = token;
      return token;
    }
  } catch (error) {
    // Do not block the request pipeline if CSRF fetch fails; the calling
    // code / middleware will surface a clear CSRF error from the API.
    console.warn('Failed to fetch CSRF token:', error);
  }

  return getCsrfToken();
}

